import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Mail, Plus, Trash2, Link as LinkIcon, UserPlus, 
  Clock, CheckCircle, XCircle, AlertCircle, Send
} from 'lucide-react';
import api from '@/lib/api';

const STATUS_LABELS = {
  pending: { label: 'In attesa', color: 'bg-yellow-500/15 text-yellow-500', icon: Clock },
  accepted: { label: 'Accettato', color: 'bg-green-500/15 text-green-500', icon: CheckCircle },
  expired: { label: 'Scaduto', color: 'bg-red-500/15 text-red-500', icon: XCircle },
  cancelled: { label: 'Annullato', color: 'bg-gray-500/15 text-gray-500', icon: AlertCircle },
};

export default function IntegratorPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [maxUsers, setMaxUsers] = useState(null);
  
  // Invites state
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Links state
  const [links, setLinks] = useState([]);
  
  // Assign dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, invitesRes, linksRes] = await Promise.all([
        api.get('/integrator/users'),
        api.get('/integrator/invites'),
        api.get('/integrator/links'),
      ]);
      
      setUsers(usersRes.data.users || []);
      setMaxUsers(usersRes.data.max_users);
      setInvites(invitesRes.data || []);
      setLinks(linksRes.data || []);
    } catch (err) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    
    setSendingInvite(true);
    try {
      await api.post('/integrator/invite', { email: inviteEmail });
      toast.success('Invito inviato!');
      setInviteEmail('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Annullare questo invito?')) return;
    try {
      await api.delete(`/integrator/invites/${inviteId}`);
      toast.success('Invito annullato');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'annullamento');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Rimuovere questo utente dal team?')) return;
    try {
      await api.delete(`/integrator/users/${userId}`);
      toast.success('Utente rimosso dal team');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleAssignLink = async () => {
    if (!selectedLink || !selectedUserId) {
      toast.error('Seleziona un utente');
      return;
    }
    
    try {
      await api.post(`/integrator/links/${selectedLink.link_id}/assign`, { 
        link_id: selectedLink.link_id,
        user_id: selectedUserId 
      });
      toast.success('OBC assegnato!');
      setShowAssignDialog(false);
      setSelectedLink(null);
      setSelectedUserId('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'assegnazione');
    }
  };

  const handleUnassignLink = async (linkId, userId) => {
    try {
      await api.post(`/integrator/links/${linkId}/unassign`, { 
        link_id: linkId,
        user_id: userId 
      });
      toast.success('Assegnazione rimossa');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? u.name : userId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Gestione Team
        </h1>
        <p className="text-muted-foreground mt-1">Invita utenti e assegna OBC</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-sm text-muted-foreground">
                Utenti {maxUsers ? `/ ${maxUsers}` : ''}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {invites.filter(i => i.status === 'pending').length}
              </p>
              <p className="text-sm text-muted-foreground">Inviti in attesa</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
              <LinkIcon className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{links.length}</p>
              <p className="text-sm text-muted-foreground">OBC disponibili</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invite Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Invita Utente
          </CardTitle>
          <CardDescription>
            Invita nuovi utenti o associa utenti esistenti al tuo team
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Email dell'utente..."
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
              className="flex-1"
              data-testid="invite-email-input"
            />
            <Button 
              onClick={handleSendInvite} 
              disabled={sendingInvite || (maxUsers && users.length >= maxUsers)}
              data-testid="send-invite-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingInvite ? 'Invio...' : 'Invita'}
            </Button>
          </div>
          {maxUsers && users.length >= maxUsers && (
            <p className="text-sm text-destructive mt-2">
              Hai raggiunto il limite di {maxUsers} utenti. Aggiorna il tuo piano per invitarne altri.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {invites.filter(i => i.status === 'pending').length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Inviti in Attesa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Inviato</TableHead>
                  <TableHead>Scade</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.filter(i => i.status === 'pending').map((invite) => (
                  <TableRow key={invite.invite_id}>
                    <TableCell className="font-medium">{invite.email}</TableCell>
                    <TableCell>{formatDate(invite.created_at)}</TableCell>
                    <TableCell>{formatDate(invite.expires_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCancelInvite(invite.invite_id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Team Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Il Mio Team
          </CardTitle>
          <CardDescription>
            Utenti associati al tuo account integratore
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Registrato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        u.subscription_status === 'active' 
                          ? 'bg-green-500/15 text-green-500' 
                          : 'bg-yellow-500/15 text-yellow-500'
                      }>
                        {u.subscription_status === 'active' ? 'Attivo' : 'Trial'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveUser(u.user_id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nessun utente nel team. Invita qualcuno!
            </p>
          )}
        </CardContent>
      </Card>

      {/* OBC Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-primary" />
            I Miei OBC
          </CardTitle>
          <CardDescription>
            Assegna i tuoi OBC agli utenti del team
          </CardDescription>
        </CardHeader>
        <CardContent>
          {links.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Assegnato a</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.link_id}>
                    <TableCell className="font-medium">{link.name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {link.url}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {link.assigned_users?.length > 0 ? (
                          link.assigned_users.map(userId => (
                            <Badge 
                              key={userId} 
                              variant="outline" 
                              className="flex items-center gap-1"
                            >
                              {getUserName(userId)}
                              <button 
                                onClick={() => handleUnassignLink(link.link_id, userId)}
                                className="ml-1 hover:text-destructive"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedLink(link);
                          setShowAssignDialog(true);
                        }}
                        disabled={users.length === 0}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Assegna
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nessun OBC disponibile. Aggiungi OBC dalla pagina principale.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna OBC</DialogTitle>
            <DialogDescription>
              Seleziona l'utente a cui assegnare "{selectedLink?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Utente</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Seleziona utente..." />
              </SelectTrigger>
              <SelectContent>
                {users
                  .filter(u => !selectedLink?.assigned_users?.includes(u.user_id))
                  .map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAssignLink} disabled={!selectedUserId}>
              Assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
